import { useCallback } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import type { RecordSection } from 'types/helios';
import { TemplatePresentationItemDisplay } from 'types/dataHelios';
import { countHeliosBrowseItems, isHeliosValueEmpty } from '../../helios/fieldValidation';

interface UseHeliosRecordDetailValidationProps {
  form: UseFormReturn<Record<string, unknown>>;
  values: RecordSection[];
  t: (key: string, options?: Record<string, unknown>) => string;
}

// `id` is the actual reference; `name` is display-only and can legitimately be
// null (e.g. records whose recordName is empty), so it must not be required.
const hasSingleBrowseValue = (value: unknown): boolean => {
  if (typeof value !== 'object' || value === null) return false;
  return 'id' in value && Boolean(value.id);
};

const isRequiredFormItemEmpty = (formItem: RecordSection['formItems'][number]['formItem'], fieldValue: unknown): boolean => {
  if (formItem.definition.displayType !== TemplatePresentationItemDisplay.Browse) {
    return isHeliosValueEmpty(fieldValue);
  }

  const browseSettings = formItem.definition.settings?.browse;
  const dataRecordColumn = formItem.definition.dataRecordColumn;

  if (browseSettings?.flowTemplateId) {
    return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0);
  }

  const isSingleBrowse = !browseSettings?.dynamicRelationId && !browseSettings?.allowMultipleSelection;
  if (isSingleBrowse) {
    return !hasSingleBrowseValue(fieldValue);
  }

  return !dataRecordColumn || countHeliosBrowseItems(dataRecordColumn, fieldValue) <= 0;
};

export const useHeliosRecordDetailValidation = ({ form, values, t }: UseHeliosRecordDetailValidationProps) => {
  const validateRequiredFields = useCallback(() => {
    let isValid = true;
    const currentValues = form.getValues();

    values.forEach((section) => {
      section.formItems.forEach(({ formItem }) => {
        const isVisible = formItem.definition.isVisible !== false;
        const isEditable = formItem.definition.isEditable !== false;
        const isRequired = formItem.definition.isRequired === true;
        const fieldName = formItem.id;
        const fieldState = form.getFieldState(fieldName);

        if (!isVisible || !isEditable || !isRequired) return;

        const fieldValue = currentValues[formItem.id];
        if (isRequiredFormItemEmpty(formItem, fieldValue)) {
          form.setError(fieldName, { type: 'required', message: t('common.required') }, { shouldFocus: false });
          isValid = false;
          return;
        }

        if (fieldState.error?.type === 'required') {
          form.clearErrors(fieldName);
        }
      });
    });

    return isValid;
  }, [form, t, values]);

  return {
    validateRequiredFields,
  };
};
